import { useState, useCallback, useContext } from "react";
import { Dropdown } from "~/components/UI";
import AcquirersContext from "./acquirersContext";
import { useTranslation } from "react-i18next";
import { SOURCE_TYPE } from "~/api/common";
import useCreateIncidentDialog from "~/components/Incidents/CreateIncident/useCreateIncidentDialog";
import { PERMISSION } from "~/api/permissions/enums";
import usePermissions from "~/hook/usePermissions";

interface AcquirerOptionsProps {
  acquirer: AcquirersApi.AcquirerDto;
}

/**
 * @memberof Acquirers
 * @component
 * @desc Acquirer options. Using a AcquirersContext.
 * @property {AcquirersApi.AcquirerDto} acquirer - Acquirer data;
 * @requires AcquirersContext
 */

const AcquirerOptions = ({ acquirer }: AcquirerOptionsProps) => {
  const [loading, setLoading] = useState(false);
  const { onEdit, onUpdate, onUpdateItem, onDelete } =
    useContext(AcquirersContext);
  const [createIncident] = useCreateIncidentDialog(SOURCE_TYPE.ACQUIRER);
  const { enabled } = acquirer;
  const { hasAll, hasAny } = usePermissions();
  const { t } = useTranslation();

  const onUpdateHandler = useCallback((res: Promise<unknown> | void) => {
    if (res) {
      setLoading(true);
      res.finally(() => {
        setLoading(false);
      });
    }
  }, []);

  const onCreatedIncident = useCallback(
    (incident: IncidentsApi.IncidentDto) => {
      if (acquirer.incidents) {
        acquirer.incidents.count++;
        acquirer.incidents.incidentIds.push(incident.incidentId);
      } else {
        acquirer.incidents = {
          count: 1,
          incidentIds: [incident.incidentId],
        };
      }
      onUpdateItem && onUpdateItem(acquirer);
    },
    [acquirer, onUpdateItem]
  );

  if (
    !hasAny([
      PERMISSION.EDIT_ACQUIRER,
      PERMISSION.DELETE_ACQUIRER,
      PERMISSION.CREATE_JOURNAL_INCIDENTS,
    ])
  ) {
    return null;
  }

  const options = [
    {
      label: enabled ? t("option.disable") : t("option.enable"),
      hidden: !hasAll(PERMISSION.EDIT_CONTRACT),
      confirmation: {
        cancelText: t("button.no"),
        okText: t("button.yes"),
        title: t("text.confirmationRequest"),
        onConfirm: () =>
          onUpdate &&
          onUpdateHandler(onUpdate(acquirer.acquirerId, { enabled: !enabled })),
      },
    },
    {
      label: t("option.edit"),
      hidden: !hasAll(PERMISSION.EDIT_ACQUIRER),
      onClick: () => onEdit && onEdit(acquirer),
    },
    {
      label: t("option.delete"),
      hidden: !hasAll(PERMISSION.DELETE_ACQUIRER),
      onClick: () => onDelete && onUpdateHandler(onDelete(acquirer)),
    },
    {
      label: t("option.createIncident"),
      hidden: !hasAll(PERMISSION.CREATE_JOURNAL_INCIDENTS),
      onClick: () => createIncident(acquirer.acquirerId, onCreatedIncident),
    },
  ];

  return <Dropdown loading={loading} options={options} />;
};

export default AcquirerOptions;
